// Resolving which file column feeds each field of the target model.
//
// DIRECTION - the mapping is keyed by FIELD, not by file column.
//
// The first version of this file was keyed the other way (column -> field), which put
// the file in charge of the conversation: the user read down a list of their own
// headers and picked a destination for each. Two things broke as a result. A required
// field with no matching column simply never appeared, so "you forgot Currency" could
// only be inferred from an error banner; and a column could only ever have one
// destination, which made a single "Budget Date" column unable to fill Budget Month and
// Budget Year at once - the commonest shape real budget files arrive in.
//
// Keyed by field, both fall out: every field of the model gets a row whether the file
// has something for it or not, and nothing stops two fields naming the same column.
//
// PRECEDENCE, strongest first:
//
//     user override  >  saved site mapping  >  automatic fuzzy match  >  unmapped
//
// (A future AI suggestion tier slots in below 'saved' and above 'auto'; it is
// represented here so the UI can already render it, but nothing produces it yet.)
import { MIN_COLUMN_MATCH_SCORE, scoreColumnAlias } from '../../../../Helpers';
import { columnHoldsFullDates } from './ImportValueParsers';
import type { ImportFieldDescriptor } from './ImportModelRegistry';

/**
 * Legacy target meaning "deliberately skip this column", written by the old
 * column-keyed mapping dialog. Nothing produces it any more - a file column is simply
 * not imported unless some field names it - but saved mappings in localStorage still
 * contain it, so it is filtered out when those are read back.
 */
export const IGNORE_TARGET = 'ignore';

/** Joins several field keys under one column in the persisted (column-keyed) form. */
export const PERSISTED_FIELD_SEPARATOR = '|';

/**
 * Column names across a sample of parsed rows, in first-seen order.
 *
 * XLSX.utils.sheet_to_json omits a key entirely when the cell is empty, so reading
 * Object.keys(rows[0]) alone silently drops any column whose first data cell is blank.
 * Use this anywhere a file's column list is needed - including when computing the
 * saved-mapping signature, so lookup and storage agree.
 */
export function getColumnUnion(
    rows: readonly Record<string, unknown>[],
    sampleSize = 50
): string[] {
    const ordered: string[] = [];
    const seen = new Set<string>();
    for (const row of rows.slice(0, sampleSize)) {
        for (const key of Object.keys(row)) {
            if (!seen.has(key)) {
                seen.add(key);
                ordered.push(key);
            }
        }
    }
    return ordered;
}

/** Where a mapping decision came from, weakest to strongest. */
export type MappingSource = 'auto' | 'ai' | 'saved' | 'user';

export interface FieldMappingEntry {
    /** File column feeding this field, or null when the file has nothing for it. */
    column: string | null;
    source: MappingSource;
    /** Match score [0, 1] for 'auto'/'ai' sources. Absent for explicit choices. */
    confidence?: number;
    /**
     * The column was taken from the other half of this field's date group and the
     * month/year is parsed out of a full date. Drives the "shared with" note in the UI.
     */
    fromSharedDate?: boolean;
}

/** Field key -> mapping. Contains an entry for EVERY mappable field. */
export type FieldMappings = Record<string, FieldMappingEntry>;

export interface BuildFieldMappingsInput {
    /** Column names as they appear in the file. */
    columns: readonly string[];
    /** Mappable fields of the chosen model (derived fields already excluded). */
    fields: readonly ImportFieldDescriptor[];
    /** Persisted column -> field key(s) from a previous upload of this file shape. */
    savedMappings?: Record<string, string | null> | undefined;
    /** Field key -> column chosen this session. An explicit null means "not in file". */
    userOverrides?: Record<string, string | null> | undefined;
    /** First values per column, used to spot a column of full dates. */
    sampleValues?: Record<string, unknown[]> | undefined;
}

/** Column headers drift between exports, so match them case/whitespace-insensitively. */
function normalizeColumnKey(name: string): string {
    return String(name ?? '').trim().toLowerCase();
}

/**
 * Turn the persisted column -> field(s) form into field -> column, resolving the stored
 * column name against the columns this file actually has.
 */
function invertSavedMappings(
    savedMappings: Record<string, string | null> | undefined,
    columns: readonly string[],
    fieldKeys: ReadonlySet<string>
): Record<string, string> {
    const byField: Record<string, string> = {};
    if (!savedMappings) return byField;

    const columnByKey = new Map(columns.map(c => [normalizeColumnKey(c), c]));

    for (const [savedColumn, target] of Object.entries(savedMappings)) {
        if (!target || target === IGNORE_TARGET) continue;
        const column = columnByKey.get(normalizeColumnKey(savedColumn));
        if (!column) continue;

        for (const fieldKey of target.split(PERSISTED_FIELD_SEPARATOR)) {
            const trimmed = fieldKey.trim();
            // Skip keys from another model, or fields this site has switched off.
            if (trimmed && fieldKeys.has(trimmed) && !byField[trimmed]) {
                byField[trimmed] = column;
            }
        }
    }
    return byField;
}

/** Best alias score for one field against one column. 0 when nothing matches. */
function scoreFieldAgainstColumn(field: ImportFieldDescriptor, column: string): number {
    let best = 0;
    for (const alias of field.aliases) {
        const score = scoreColumnAlias(column, alias);
        if (score > best) best = score;
        if (best >= 1) break;
    }
    return best;
}

/** The other fields sharing a field's date group. */
function dateGroupSiblings(
    field: ImportFieldDescriptor,
    fields: readonly ImportFieldDescriptor[]
): ImportFieldDescriptor[] {
    if (!field.dateGroup) return [];
    return fields.filter(f => f.key !== field.key && f.dateGroup === field.dateGroup);
}

/**
 * Resolve every mappable field to a column. Fields with nothing to map get
 * `{ column: null }` rather than being omitted - the mapping table lists the whole
 * model, and a required field with no column is filled in on the review step instead.
 */
export function buildFieldMappings(input: BuildFieldMappingsInput): FieldMappings {
    const { columns, fields, savedMappings, userOverrides, sampleValues } = input;

    const fieldKeys = new Set(fields.map(f => f.key));
    const savedByField = invertSavedMappings(savedMappings, columns, fieldKeys);

    const result: FieldMappings = {};
    /** Fields the user has decided on, including "not in file" - never auto-filled. */
    const userDecided = new Set<string>();
    /** Columns already spoken for, so auto-matching doesn't double-assign one. */
    const claimed = new Set<string>();

    // 1. Explicit decisions first, in precedence order, so they own their columns
    //    before the fuzzy matcher gets a look in.
    for (const field of fields) {
        const override = userOverrides
            && Object.prototype.hasOwnProperty.call(userOverrides, field.key)
            ? userOverrides[field.key]
            : undefined;

        if (override !== undefined) {
            userDecided.add(field.key);
            const column = override && columns.includes(override) ? override : null;
            result[field.key] = { column, source: 'user' };
            if (column) claimed.add(column);
        }
    }

    for (const field of fields) {
        if (result[field.key]) continue;
        const saved = savedByField[field.key];
        if (saved) {
            result[field.key] = { column: saved, source: 'saved' };
            claimed.add(saved);
        }
    }

    // 2. Fuzzy match what is left, best score first, so a strong match wins a contested
    //    column over a weak one regardless of field order.
    const candidates: { field: ImportFieldDescriptor; column: string; score: number }[] = [];
    for (const field of fields) {
        if (result[field.key]) continue;
        for (const column of columns) {
            const score = scoreFieldAgainstColumn(field, column);
            if (score >= MIN_COLUMN_MATCH_SCORE) candidates.push({ field, column, score });
        }
    }
    candidates.sort((a, b) => b.score - a.score);

    for (const candidate of candidates) {
        if (result[candidate.field.key] || claimed.has(candidate.column)) continue;
        result[candidate.field.key] = {
            column: candidate.column,
            source: 'auto',
            confidence: candidate.score,
        };
        claimed.add(candidate.column);
    }

    for (const field of fields) {
        if (!result[field.key]) result[field.key] = { column: null, source: 'auto' };
    }

    // 3. Month/year from one date column. When half a date group resolved to a column of
    //    full dates and the other half found nothing, feed both from that column - the
    //    month field parses the month out and the year field the year. Files carrying a
    //    single "Budget Date" instead of separate month and year columns are the norm,
    //    and without this pass the year half looks like a missing required field.
    for (const field of fields) {
        const entry = result[field.key];
        if (entry.column || userDecided.has(field.key)) continue;

        for (const sibling of dateGroupSiblings(field, fields)) {
            const siblingColumn = result[sibling.key]?.column;
            if (!siblingColumn) continue;
            if (!columnHoldsFullDates(sampleValues?.[siblingColumn])) continue;

            result[field.key] = {
                column: siblingColumn,
                source: 'auto',
                confidence: result[sibling.key].confidence,
                fromSharedDate: true,
            };
            break;
        }
    }

    return result;
}

/** Field key -> column for every field that has one. */
export function getMappedColumnsByField(mappings: FieldMappings): Record<string, string> {
    const byField: Record<string, string> = {};
    for (const [fieldKey, entry] of Object.entries(mappings)) {
        if (entry.column) byField[fieldKey] = entry.column;
    }
    return byField;
}

/** File columns no field reads - they are simply not imported. */
export function getUnusedColumns(
    mappings: FieldMappings,
    columns: readonly string[]
): string[] {
    const used = new Set(Object.values(mappings).map(e => e.column).filter(Boolean));
    return columns.filter(column => !used.has(column));
}

/**
 * Fields fed by a given column. More than one is legitimate for the month/year halves
 * of a date group and suspect anywhere else, which is what the UI says.
 */
export function getFieldsUsingColumn(
    mappings: FieldMappings,
    column: string
): string[] {
    return Object.entries(mappings)
        .filter(([, entry]) => entry.column === column)
        .map(([fieldKey]) => fieldKey);
}

/**
 * Required fields with no column feeding them. NOT an error: the file legitimately may
 * not carry a column for a required field (one currency for the whole file, one budget
 * round, ...), and the value is then filled in on the review step - per row or with the
 * bulk-edit toolbar. The UI surfaces these so the user knows what is waiting there.
 */
export function getMissingRequiredFields(
    mappings: FieldMappings,
    fields: readonly ImportFieldDescriptor[]
): ImportFieldDescriptor[] {
    return fields.filter(
        field => field.required && !field.derived && !mappings[field.key]?.column
    );
}

/**
 * Flatten to the column -> field(s) shape used for persistence, so it stays compatible
 * with the store the legacy mapping dialog reads and writes. A column feeding several
 * fields is joined with PERSISTED_FIELD_SEPARATOR.
 */
export function toPersistableMappings(mappings: FieldMappings): Record<string, string> {
    const byColumn: Record<string, string[]> = {};
    for (const [fieldKey, entry] of Object.entries(mappings)) {
        if (!entry.column) continue;
        (byColumn[entry.column] ??= []).push(fieldKey);
    }

    const flat: Record<string, string> = {};
    for (const [column, fieldKeys] of Object.entries(byColumn)) {
        flat[column] = fieldKeys.join(PERSISTED_FIELD_SEPARATOR);
    }
    return flat;
}
